import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage } from '../../util/reactIntl';
import { getEcolabelFilterOptions } from '../../config/ecolabels';
import { useValuePrioritiesEditor } from '../../hooks/useValuePriorities';

import css from './ValuePriorities.module.css';

/**
 * "Your priorities": the shopper picks the ecolabels that matter most to them.
 *
 * Each ecolabel is a toggle button and changes are saved as soon as they're made,
 * so there's no form to submit. The same choice then drives the "Matches 2 of your
 * 3 priorities" line on listing cards and the product page, and the "Show products
 * with my priorities" shortcut on the browse page.
 *
 * The options follow the hosted ecolabel field (like the browse filter), so a
 * shopper can only pick ecolabels products can actually carry.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.id]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const ValuePrioritiesPicker = props => {
  const { id, className } = props;
  const config = useConfiguration();
  const { priorities, setPriorities, isLoggedIn } = useValuePrioritiesEditor();
  const { options } = getEcolabelFilterOptions(config?.listing?.listingFields);

  const toggle = key =>
    setPriorities(
      priorities.includes(key) ? priorities.filter(k => k !== key) : [...priorities, key]
    );

  return (
    <div id={id} className={classNames(css.picker, className)}>
      <p className={css.pickerIntro}>
        <FormattedMessage id="ValuePriorities.intro" />
      </p>
      <ul className={css.options}>
        {options.map(({ ecolabel, label }) => {
          const isSelected = priorities.includes(ecolabel.key);
          return (
            <li key={ecolabel.key} className={css.option}>
              <button
                type="button"
                className={classNames(css.optionButton, { [css.optionSelected]: isSelected })}
                aria-pressed={isSelected}
                onClick={() => toggle(ecolabel.key)}
              >
                <img className={css.optionBadge} src={ecolabel.src} alt="" loading="lazy" />
                <span className={css.optionLabel}>{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className={css.pickerFooter}>
        <span className={css.pickerNote}>
          <FormattedMessage
            id={isLoggedIn ? 'ValuePriorities.savedToAccount' : 'ValuePriorities.savedOnDevice'}
          />
        </span>
        {priorities.length > 0 ? (
          <button type="button" className={css.clearButton} onClick={() => setPriorities([])}>
            <FormattedMessage id="ValuePriorities.clear" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default ValuePrioritiesPicker;
